import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

// Formatta ora da timestamp - gestisce sia ISO UTC che locale
function formatTime(isoString) {
  if (!isoString) return '-';
  try {
    if (isoString.includes('T') && !isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
      return isoString.split('T')[1].substring(0, 5);
    }
    return new Date(isoString).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
}
import {
  Users, Building2, Wrench, AlertTriangle, Package, CalendarDays,
  Clock, FileWarning, CheckCircle, XCircle, ArrowRight, TrendingUp,
  Calendar, StickyNote, Megaphone, Plus, Trash2, Edit, ExternalLink, MapPin,
  UserCheck, ChevronLeft, ChevronRight as ChevronRightIcon, Check
} from 'lucide-react';

function KpiCard({ icon: Icon, label, value, sub, color, onClick, testId }) {
  return (
    <Card
      className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
      onClick={onClick}
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon size={20} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TrafficLight({ valid, expiring, expired }) {
  const total = valid + expiring + expired;
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded-full bg-green-500" />
        <span className="text-sm font-medium text-slate-700">{valid}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded-full bg-amber-500" />
        <span className="text-sm font-medium text-slate-700">{expiring}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-500" />
        <span className="text-sm font-medium text-slate-700">{expired}</span>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    scaduto: { label: 'Scaduto', cls: 'bg-red-50 text-red-700 border-red-200' },
    in_scadenza: { label: 'In Scadenza', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    valido: { label: 'Valido', cls: 'bg-green-50 text-green-700 border-green-200' },
  };
  const s = map[status] || map.valido;
  return <Badge variant="outline" className={s.cls}>{s.label}</Badge>;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Calendar, Notes, Bulletin states
  const [events, setEvents] = useState([]);
  const [notes, setNotes] = useState('');
  const [bulletinPosts, setBulletinPosts] = useState([]);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showBulletinDialog, setShowBulletinDialog] = useState(false);
  const [eventForm, setEventForm] = useState({ title: '', date: '', time: '', end_time: '', location: '', meeting_link: '', description: '' });
  const [bulletinForm, setBulletinForm] = useState({ title: '', content: '', priority: 'normal' });
  const [notesSaving, setNotesSaving] = useState(false);

  // Availability Calendar states
  const [availabilityData, setAvailabilityData] = useState({});
  const [availMonth, setAvailMonth] = useState(new Date().getMonth() + 1);
  const [availYear, setAvailYear] = useState(new Date().getFullYear());
  
  // Incidents/Non-conformities state
  const [incidentAlerts, setIncidentAlerts] = useState([]);
  // Safety expiry state
  const [safetyExpiry, setSafetyExpiry] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, alertsRes, eventsRes, notesRes, bulletinRes, availRes, incidentsRes, safetyRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/expiry-alerts'),
          api.get('/calendar/events').catch(() => ({ data: [] })),
          api.get('/calendar/notes').catch(() => ({ data: { content: '' } })),
          api.get('/calendar/bulletin').catch(() => ({ data: [] })),
          api.get(`/availability/calendar?month=${availMonth}&year=${availYear}`).catch(() => ({ data: {} })),
          api.get('/incidents/dashboard-alerts').catch(() => ({ data: [] })),
          api.get('/dashboard/safety-expiry').catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setAlerts(alertsRes.data.slice(0, 10));
        setEvents(eventsRes.data.slice(0, 5));
        setNotes(notesRes.data?.content || '');
        setBulletinPosts(bulletinRes.data.slice(0, 5));
        setAvailabilityData(availRes.data);
        setIncidentAlerts(incidentsRes.data.slice(0, 5));
        setSafetyExpiry(safetyRes.data);

        if (user?.employee_id) {
          try {
            const attRes = await api.get('/attendance/my-today');
            setTodayAttendance(attRes.data);
          } catch {}
        }
      } catch (err) {
        console.error('Dashboard load error', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, availMonth, availYear]);

  const dismissAlert = async (alertKey) => {
    setAlerts(prev => prev.filter(a => a.alert_key !== alertKey));
    try {
      await api.post('/dashboard/expiry-alerts/dismiss', { alert_key: alertKey });
    } catch (err) {
      console.error('Dismiss alert error', err);
    }
  };

  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      await api.put('/calendar/notes', { content: notes });
    } catch (err) {
      console.error(err);
    } finally {
      setNotesSaving(false);
    }
  };

  const createEvent = async () => {
    try {
      await api.post('/calendar/events', eventForm);
      setShowEventDialog(false);
      setEventForm({ title: '', date: '', time: '', end_time: '', location: '', meeting_link: '', description: '' });
      const res = await api.get('/calendar/events');
      setEvents(res.data.slice(0, 5));
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Eliminare questo evento?')) return;
    try {
      await api.delete(`/calendar/events/${eventId}`);
      setEvents(events.filter(e => e.id !== eventId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const createBulletinPost = async () => {
    try {
      await api.post('/calendar/bulletin', bulletinForm);
      setShowBulletinDialog(false);
      setBulletinForm({ title: '', content: '', priority: 'normal' });
      const res = await api.get('/calendar/bulletin');
      setBulletinPosts(res.data.slice(0, 5));
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const deleteBulletinPost = async (postId) => {
    if (!window.confirm('Eliminare questo messaggio?')) return;
    try {
      await api.delete(`/calendar/bulletin/${postId}`);
      setBulletinPosts(bulletinPosts.filter(p => p.id !== postId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-slate-200 rounded-lg" />)}
        </div>
      </div>
    );
  }

  const isEmployee = user?.role_name === 'dipendente';

  // Employee Dashboard
  if (isEmployee) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="employee-dashboard">
        <h1 className="text-2xl font-bold text-slate-900">Benvenuto</h1>

        {/* Clock In/Out - 4 Step Flow */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Clock size={32} className="text-slate-400" />
              <div className="flex-1 text-center sm:text-left">
                {todayAttendance?.clock_in ? (
                  <div className="space-y-0.5">
                    <p className="text-sm text-slate-500">
                      Entrata: {formatTime(todayAttendance.clock_in)}
                    </p>
                    {todayAttendance.break_start && (
                      <p className="text-sm text-slate-500">
                        Pausa: {formatTime(todayAttendance.break_start)}
                        {todayAttendance.break_end
                          ? ` - ${formatTime(todayAttendance.break_end)}`
                          : ' (in corso)'}
                        {todayAttendance.break_minutes != null && ` (${todayAttendance.break_minutes} min)`}
                      </p>
                    )}
                    {todayAttendance.clock_out && (
                      <p className="text-sm text-slate-500">
                        Uscita: {formatTime(todayAttendance.clock_out)}
                        {todayAttendance.hours_worked && ` | ${todayAttendance.hours_worked}h nette`}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Nessuna timbratura oggi</p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap justify-center">
                {/* Step 1: Timbra Entrata */}
                {!todayAttendance?.clock_in && (
                  <Button onClick={async () => { try { await api.post('/attendance/clock-in', {}); window.location.reload(); } catch(e) { alert(e.response?.data?.detail || 'Errore'); } }} data-testid="clock-in-btn" className="bg-green-600 hover:bg-green-700">
                    Timbra Entrata
                  </Button>
                )}
                {/* Step 2: Inizio Pausa */}
                {todayAttendance?.clock_in && !todayAttendance?.break_start && !todayAttendance?.clock_out && (
                  <Button onClick={async () => { try { await api.post('/attendance/break-start', {}); window.location.reload(); } catch(e) { alert(e.response?.data?.detail || 'Errore'); } }} data-testid="break-start-btn" className="bg-amber-500 hover:bg-amber-600">
                    Inizio Pausa
                  </Button>
                )}
                {/* Step 3: Fine Pausa */}
                {todayAttendance?.break_start && !todayAttendance?.break_end && !todayAttendance?.clock_out && (
                  <Button onClick={async () => { try { await api.post('/attendance/break-end', {}); window.location.reload(); } catch(e) { alert(e.response?.data?.detail || 'Errore'); } }} data-testid="break-end-btn" className="bg-amber-600 hover:bg-amber-700">
                    Fine Pausa
                  </Button>
                )}
                {/* Step 4: Timbra Uscita (dopo pausa completata o saltata) */}
                {todayAttendance?.clock_in && !todayAttendance?.clock_out && (todayAttendance?.break_end || !todayAttendance?.break_start) && (
                  <Button onClick={async () => { try { await api.post('/attendance/clock-out', {}); window.location.reload(); } catch(e) { alert(e.response?.data?.detail || 'Errore'); } }} data-testid="clock-out-btn" variant="destructive">
                    Timbra Uscita
                  </Button>
                )}
                {todayAttendance?.clock_out && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-4 py-2"><CheckCircle size={14} className="mr-1" />Completata</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/leaves')}>
            <CardContent className="p-5">
              <CalendarDays size={20} className="text-slate-400 mb-2" />
              <p className="text-sm font-medium text-slate-900">Ferie e Permessi</p>
              <p className="text-xs text-slate-500 mt-1">Richiedi o visualizza</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/attendance')}>
            <CardContent className="p-5">
              <Clock size={20} className="text-slate-400 mb-2" />
              <p className="text-sm font-medium text-slate-900">Le mie presenze</p>
              <p className="text-xs text-slate-500 mt-1">Storico timbrature</p>
            </CardContent>
          </Card>
        </div>

        {/* Calendar, Notes, Bulletin for employees too */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Calendar */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Calendar size={16} className="text-blue-600" />Calendario</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowEventDialog(true)}><Plus size={14} /></Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {events.length === 0 ? (
                <p className="text-xs text-slate-500 py-4 text-center">Nessun evento</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {events.map(ev => (
                    <div key={ev.id} className="p-2 bg-slate-50 rounded text-xs group">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{ev.title}</p>
                          <p className="text-slate-500">{ev.date} {ev.time && `alle ${ev.time}`}</p>
                          {ev.location && <p className="text-slate-400"><MapPin size={10} className="inline mr-1" />{ev.location}</p>}
                        </div>
                        <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 p-1 h-auto" onClick={() => deleteEvent(ev.id)}><Trash2 size={12} /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><StickyNote size={16} className="text-amber-600" />Blocco Note</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={saveNotes}
                placeholder="Scrivi le tue note..."
                className="min-h-[120px] text-xs resize-none"
                data-testid="personal-notes"
              />
              {notesSaving && <p className="text-[10px] text-slate-400 mt-1">Salvataggio...</p>}
            </CardContent>
          </Card>

          {/* Bulletin */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Megaphone size={16} className="text-red-600" />Bacheca</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowBulletinDialog(true)}><Plus size={14} /></Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {bulletinPosts.length === 0 ? (
                <p className="text-xs text-slate-500 py-4 text-center">Nessun messaggio</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {bulletinPosts.map(post => (
                    <div key={post.id} className={`p-2 rounded text-xs group ${post.priority === 'urgent' ? 'bg-red-50 border border-red-200' : post.priority === 'important' ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          {post.title && <p className="font-medium text-slate-900">{post.title}</p>}
                          <p className="text-slate-700">{post.content}</p>
                          <p className="text-slate-400 text-[10px] mt-1">{post.author_email} - {new Date(post.created_at).toLocaleDateString('it-IT')}</p>
                        </div>
                        {(post.author_id === user?.id || user?.role_name === 'admin') && (
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 p-1 h-auto" onClick={() => deleteBulletinPost(post.id)}><Trash2 size={12} /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Incidents/Non-Conformities Alerts */}
        {(user?.role_name === 'admin' || user?.role_name === 'qhse') && incidentAlerts.length > 0 && (
          <Card className="border-orange-200 shadow-sm bg-orange-50/30">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle size={16} className="text-orange-600" />
                  Segnalazioni Aperte
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => navigate('/incidents')} className="text-orange-600 border-orange-300 hover:bg-orange-100">
                  <ArrowRight size={14} className="mr-1" /> Vedi tutte
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {incidentAlerts.map(inc => {
                  const typeColors = {
                    incidente: 'bg-red-100 text-red-700 border-red-200',
                    near_miss: 'bg-amber-100 text-amber-700 border-amber-200',
                    non_conformita: 'bg-orange-100 text-orange-700 border-orange-200'
                  };
                  const severityColors = {
                    critica: 'bg-red-600 text-white',
                    alta: 'bg-orange-500 text-white',
                    media: 'bg-yellow-500 text-white',
                    bassa: 'bg-green-500 text-white'
                  };
                  return (
                    <div key={inc.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-orange-200 hover:border-orange-300 cursor-pointer" onClick={() => navigate('/incidents')}>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${typeColors[inc.type] || 'bg-slate-100'}`}>
                          {inc.type_label}
                        </span>
                        <span className="text-sm font-medium text-slate-800">{inc.title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${severityColors[inc.severity] || 'bg-slate-400'}`}>
                          {inc.severity?.toUpperCase()}
                        </span>
                        {inc.is_overdue ? (
                          <span className="text-[10px] font-medium text-red-600">SCADUTA</span>
                        ) : inc.days_remaining !== null && inc.days_remaining <= 7 ? (
                          <span className="text-[10px] text-amber-600">{inc.days_remaining}gg</span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Event Dialog */}
        <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Nuovo Evento</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">Titolo *</Label><Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Data *</Label><Input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Ora</Label><Input type="time" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Luogo</Label><Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder="Es. Ufficio, Cantiere..." /></div>
              <div className="space-y-1.5"><Label className="text-xs">Link Riunione</Label><Input value={eventForm.meeting_link} onChange={(e) => setEventForm({ ...eventForm, meeting_link: e.target.value })} placeholder="https://..." /></div>
              <div className="space-y-1.5"><Label className="text-xs">Descrizione</Label><Textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} className="resize-none" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowEventDialog(false)}>Annulla</Button><Button onClick={createEvent} disabled={!eventForm.title || !eventForm.date}>Crea</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulletin Dialog */}
        <Dialog open={showBulletinDialog} onOpenChange={setShowBulletinDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Nuovo Messaggio Bacheca</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">Titolo (opzionale)</Label><Input value={bulletinForm.title} onChange={(e) => setBulletinForm({ ...bulletinForm, title: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Messaggio *</Label><Textarea value={bulletinForm.content} onChange={(e) => setBulletinForm({ ...bulletinForm, content: e.target.value })} className="resize-none min-h-[100px]" /></div>
              <div className="space-y-1.5">
                <Label className="text-xs">Priorita</Label>
                <div className="flex gap-2">
                  {['normal', 'important', 'urgent'].map(p => (
                    <Button key={p} variant={bulletinForm.priority === p ? 'default' : 'outline'} size="sm" onClick={() => setBulletinForm({ ...bulletinForm, priority: p })} className="text-xs">
                      {p === 'normal' ? 'Normale' : p === 'important' ? 'Importante' : 'Urgente'}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowBulletinDialog(false)}>Annulla</Button><Button onClick={createBulletinPost} disabled={!bulletinForm.content}>Pubblica</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Admin / QHSE / Capo Cantiere Dashboard
  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
        <TrafficLight
          valid={stats?.documents_valid || 0}
          expiring={stats?.documents_expiring || 0}
          expired={stats?.documents_expired || 0}
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Users} label="Dipendenti Attivi" value={stats?.total_employees || 0} color="bg-blue-50 text-blue-600" onClick={() => navigate('/employees')} testId="kpi-employees" />
        <KpiCard icon={Building2} label="Cantieri Attivi" value={stats?.active_sites || 0} color="bg-emerald-50 text-emerald-600" onClick={() => navigate('/sites')} testId="kpi-sites" />
        <KpiCard icon={Wrench} label="In Manutenzione" value={stats?.equip_maintenance || 0} sub={`${stats?.total_equipment || 0} totali`} color="bg-orange-50 text-orange-600" onClick={() => navigate('/equipment')} testId="kpi-equipment" />
        <KpiCard icon={Package} label="Scorte Basse" value={stats?.low_stock_items || 0} color="bg-red-50 text-red-600" onClick={() => navigate('/inventory')} testId="kpi-stock" />
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Expiry Alerts */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Scadenze Imminenti</CardTitle>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{alerts.length}</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {alerts.length === 0 ? (
              <div className="flex items-center gap-2 text-green-600 py-4">
                <CheckCircle size={16} />
                <span className="text-sm">Nessuna scadenza imminente</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {alerts.map((a, i) => (
                  <div key={a.alert_key || i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${a.status === 'scaduto' ? 'bg-red-500' : 'bg-amber-500'}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{a.item}</p>
                        <p className="text-xs text-slate-500">{a.type} - {a.reference}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-xs text-slate-500">{a.days_remaining}gg</span>
                      <StatusBadge status={a.status} />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-slate-400 hover:text-green-600"
                        title="Segna come letta"
                        onClick={() => dismissAlert(a.alert_key)}
                        data-testid={`dismiss-alert-${a.alert_key}`}
                      >
                        <Check size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Attendance & Leaves Summary */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Oggi</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Presenze</p>
                  <p className="text-xs text-slate-500">{stats?.today_present || 0} presenti su {stats?.total_employees || 0}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/attendance')} data-testid="goto-attendance">
                <ArrowRight size={14} />
              </Button>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CalendarDays size={18} className="text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Richieste in attesa</p>
                  <p className="text-xs text-slate-500">{stats?.pending_leaves || 0} da approvare</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/leaves')} data-testid="goto-leaves">
                <ArrowRight size={14} />
              </Button>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <FileWarning size={18} className="text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Documenti scaduti</p>
                  <p className="text-xs text-slate-500">{stats?.documents_expired || 0} elementi</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Incidents/Non-Conformities Alerts for Admin Dashboard */}
      {incidentAlerts.length > 0 && (
        <Card className="border-orange-200 shadow-sm bg-orange-50/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle size={16} className="text-orange-600" />
                Segnalazioni Aperte
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => navigate('/incidents')} className="text-orange-600 border-orange-300 hover:bg-orange-100" data-testid="incidents-alert-link">
                <ArrowRight size={14} className="mr-1" /> Vedi tutte
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {incidentAlerts.map(inc => {
                const typeColors = {
                  incidente: 'bg-red-100 text-red-700 border-red-200',
                  near_miss: 'bg-amber-100 text-amber-700 border-amber-200',
                  non_conformita: 'bg-orange-100 text-orange-700 border-orange-200'
                };
                const severityColors = {
                  critica: 'bg-red-600 text-white',
                  alta: 'bg-orange-500 text-white',
                  media: 'bg-yellow-500 text-white',
                  bassa: 'bg-green-500 text-white'
                };
                return (
                  <div key={inc.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-orange-200 hover:border-orange-300 cursor-pointer" onClick={() => navigate('/incidents')} data-testid={`incident-alert-${inc.id}`}>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${typeColors[inc.type] || 'bg-slate-100'}`}>
                        {inc.type_label}
                      </span>
                      <span className="text-sm font-medium text-slate-800">{inc.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${severityColors[inc.severity] || 'bg-slate-400'}`}>
                        {inc.severity?.toUpperCase()}
                      </span>
                      {inc.is_overdue ? (
                        <span className="text-[10px] font-medium text-red-600">SCADUTA</span>
                      ) : inc.days_remaining !== null && inc.days_remaining <= 7 ? (
                        <span className="text-[10px] text-amber-600">{inc.days_remaining}gg</span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cruscotto Scadenze Sicurezza */}
      {safetyExpiry && (
        <Card className="border-slate-200 shadow-sm" data-testid="safety-expiry-widget">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle size={16} className="text-slate-600" />
                Scadenze Sicurezza
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => navigate('/safety-devices')} className="gap-1" data-testid="goto-safety-devices">
                <ArrowRight size={14} /> Dispositivi
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            {/* Summary per tipo dispositivo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(safetyExpiry.summary || {}).map(([key, data]) => (
                <div key={key} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{data.label}</p>
                  <p className="text-lg font-bold text-slate-900">{data.total}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {data.scaduto > 0 && <span className="text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{data.scaduto} scad.</span>}
                    {data.in_scadenza > 0 && <span className="text-xs font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">{data.in_scadenza} in scad.</span>}
                    {data.scaduto === 0 && data.in_scadenza === 0 && <span className="text-xs text-green-600">Tutto OK</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Lista elementi critici */}
            {safetyExpiry.critical_items?.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 border-b">
                  <p className="text-xs font-medium text-slate-600">Dispositivi in scadenza / scaduti</p>
                </div>
                <div className="divide-y max-h-48 overflow-y-auto">
                  {safetyExpiry.critical_items.map(item => (
                    <div key={item.id} className="flex items-center justify-between px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.status === 'scaduto' ? 'bg-red-500' : 'bg-amber-500'}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{item.name}</p>
                          <p className="text-xs text-slate-500">{item.device_type_label} {item.code ? `- ${item.code}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-xs text-slate-500">{item.days_remaining}gg</span>
                        <StatusBadge status={item.status} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(!safetyExpiry.critical_items || safetyExpiry.critical_items.length === 0) && (
              <div className="flex items-center gap-2 text-green-600 py-2">
                <CheckCircle size={16} />
                <span className="text-sm">Nessun dispositivo in scadenza</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Availability Calendar */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <UserCheck size={16} className="text-purple-600" />
              Calendario Reperibilita
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => {
                if (availMonth === 1) {
                  setAvailMonth(12);
                  setAvailYear(availYear - 1);
                } else {
                  setAvailMonth(availMonth - 1);
                }
              }}><ChevronLeft size={16} /></Button>
              <span className="text-sm font-medium min-w-[120px] text-center">
                {new Date(availYear, availMonth - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
              </span>
              <Button variant="ghost" size="sm" onClick={() => {
                if (availMonth === 12) {
                  setAvailMonth(1);
                  setAvailYear(availYear + 1);
                } else {
                  setAvailMonth(availMonth + 1);
                }
              }}><ChevronRightIcon size={16} /></Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/work-journal')}>
                <ArrowRight size={14} className="mr-1" /> Giornale
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Legenda */}
          <div className="flex flex-wrap gap-3 mb-3 text-[10px]">
            <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></span> Straordinario</div>
            <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-100 border border-purple-300"></span> Notturno</div>
            <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></span> Sabato</div>
            <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-100 border border-orange-300"></span> Domenica</div>
            <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-300"></span> Festivo</div>
          </div>
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 text-xs">
            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => (
              <div key={d} className="text-center font-medium text-slate-500 py-1">{d}</div>
            ))}
            {(() => {
              const firstDay = new Date(availYear, availMonth - 1, 1);
              const lastDay = new Date(availYear, availMonth, 0);
              const startPadding = (firstDay.getDay() + 6) % 7; // Monday = 0
              const days = [];
              
              // Padding for start
              for (let i = 0; i < startPadding; i++) {
                days.push(<div key={`pad-${i}`} className="h-16"></div>);
              }
              
              // Days of month
              for (let d = 1; d <= lastDay.getDate(); d++) {
                const dateStr = `${availYear}-${String(availMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                const dayData = availabilityData[dateStr] || [];
                const isToday = dateStr === new Date().toISOString().split('T')[0];
                
                days.push(
                  <div key={dateStr} className={`min-h-[64px] p-1 border rounded ${isToday ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}>
                    <div className="text-[10px] font-medium text-slate-600 mb-1">{d}</div>
                    <div className="space-y-0.5 max-h-[44px] overflow-y-auto">
                      {dayData.slice(0, 3).map((entry, idx) => {
                        const typeColors = {
                          straordinario: 'bg-blue-100 text-blue-700',
                          notturno: 'bg-purple-100 text-purple-700',
                          sabato: 'bg-amber-100 text-amber-700',
                          domenica: 'bg-orange-100 text-orange-700',
                          festivo: 'bg-red-100 text-red-700',
                        };
                        return (
                          <div key={idx} className={`px-1 py-0.5 rounded text-[9px] truncate ${typeColors[entry.availability_type] || 'bg-slate-100 text-slate-700'}`} title={`${entry.employee_name} - ${entry.availability_type}`}>
                            {entry.employee_name?.split(' ')[0]}
                          </div>
                        );
                      })}
                      {dayData.length > 3 && (
                        <div className="text-[9px] text-slate-500 text-center">+{dayData.length - 3}</div>
                      )}
                    </div>
                  </div>
                );
              }
              return days;
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Calendar, Notes, Bulletin Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendar */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Calendar size={16} className="text-blue-600" />Calendario Aziendale</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowEventDialog(true)} data-testid="add-event-btn"><Plus size={14} /></Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {events.length === 0 ? (
              <p className="text-xs text-slate-500 py-4 text-center">Nessun evento in programma</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {events.map(ev => (
                  <div key={ev.id} className="p-2 bg-slate-50 rounded text-xs group hover:bg-slate-100 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{ev.title}</p>
                        <p className="text-slate-500">{ev.date} {ev.time && `alle ${ev.time}`}</p>
                        {ev.location && <p className="text-slate-400"><MapPin size={10} className="inline mr-1" />{ev.location}</p>}
                        {ev.meeting_link && <a href={ev.meeting_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-1"><ExternalLink size={10} />Link riunione</a>}
                      </div>
                      <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 p-1 h-auto text-red-600 hover:text-red-800" onClick={() => deleteEvent(ev.id)}><Trash2 size={12} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><StickyNote size={16} className="text-amber-600" />Blocco Note Personale</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={saveNotes}
              placeholder="Scrivi le tue note personali..."
              className="min-h-[140px] text-xs resize-none"
              data-testid="personal-notes"
            />
            {notesSaving && <p className="text-[10px] text-slate-400 mt-1">Salvataggio automatico...</p>}
          </CardContent>
        </Card>

        {/* Bulletin */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Megaphone size={16} className="text-red-600" />Bacheca Aziendale</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowBulletinDialog(true)} data-testid="add-bulletin-btn"><Plus size={14} /></Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {bulletinPosts.length === 0 ? (
              <p className="text-xs text-slate-500 py-4 text-center">Nessun messaggio in bacheca</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {bulletinPosts.map(post => (
                  <div key={post.id} className={`p-2 rounded text-xs group ${post.priority === 'urgent' ? 'bg-red-50 border border-red-200' : post.priority === 'important' ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {post.title && <p className="font-medium text-slate-900">{post.title}</p>}
                        <p className="text-slate-700 whitespace-pre-wrap">{post.content}</p>
                        <p className="text-slate-400 text-[10px] mt-1">{post.author_email} - {new Date(post.created_at).toLocaleDateString('it-IT')}</p>
                      </div>
                      {(post.author_id === user?.id || user?.role_name === 'admin') && (
                        <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 p-1 h-auto text-red-600 hover:text-red-800" onClick={() => deleteBulletinPost(post.id)}><Trash2 size={12} /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuovo Evento</DialogTitle><DialogDescription>Crea un evento visibile a tutti gli utenti</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Titolo *</Label><Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} data-testid="event-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Data *</Label><Input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} data-testid="event-date" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Ora</Label><Input type="time" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Luogo</Label><Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder="Es. Ufficio, Cantiere X..." /></div>
            <div className="space-y-1.5"><Label className="text-xs">Link Riunione</Label><Input value={eventForm.meeting_link} onChange={(e) => setEventForm({ ...eventForm, meeting_link: e.target.value })} placeholder="https://meet.google.com/..." /></div>
            <div className="space-y-1.5"><Label className="text-xs">Descrizione</Label><Textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} className="resize-none" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowEventDialog(false)}>Annulla</Button><Button onClick={createEvent} disabled={!eventForm.title || !eventForm.date} data-testid="event-save-btn">Crea Evento</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulletin Dialog */}
      <Dialog open={showBulletinDialog} onOpenChange={setShowBulletinDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuovo Messaggio Bacheca</DialogTitle><DialogDescription>Scrivi un messaggio visibile a tutti</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Titolo (opzionale)</Label><Input value={bulletinForm.title} onChange={(e) => setBulletinForm({ ...bulletinForm, title: e.target.value })} data-testid="bulletin-title" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Messaggio *</Label><Textarea value={bulletinForm.content} onChange={(e) => setBulletinForm({ ...bulletinForm, content: e.target.value })} className="resize-none min-h-[100px]" data-testid="bulletin-content" /></div>
            <div className="space-y-1.5">
              <Label className="text-xs">Priorita</Label>
              <div className="flex gap-2">
                {['normal', 'important', 'urgent'].map(p => (
                  <Button key={p} variant={bulletinForm.priority === p ? 'default' : 'outline'} size="sm" onClick={() => setBulletinForm({ ...bulletinForm, priority: p })} className="text-xs">
                    {p === 'normal' ? 'Normale' : p === 'important' ? 'Importante' : 'Urgente'}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowBulletinDialog(false)}>Annulla</Button><Button onClick={createBulletinPost} disabled={!bulletinForm.content} data-testid="bulletin-save-btn">Pubblica</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
